import { useCallback, useEffect, useState } from 'react';
import { icons } from '../../components/ui/Icons';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorState } from '../../components/ui/ErrorState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { SettingEntry } from '../../types/auth';
import { requestJson, getErrorMessage, formatDate } from '../../lib/adminApi';

export function SettingsPage() {
  const [settings, setSettings] = useState<SettingEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await requestJson<SettingEntry[]>(`/settings`);
      setSettings(Array.isArray(data) ? data : []);
    } catch (error) {
      setError(getErrorMessage(error, 'Systemeinstellungen konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const breadcrumbItems = [
    { label: 'Administration', href: '/admin' },
    { label: 'Organisation', href: '/admin/organization' },
    { label: 'Einstellungen', current: true },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
              <icons.Settings className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <CardTitle>Systemeinstellungen</CardTitle>
              <CardDescription>
                Vorhandene Einträge aus der bestehenden settings-Tabelle werden lesend angezeigt.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && settings.length === 0 ? (
            <ErrorState description={error} onRetry={() => void fetchSettings()} />
          ) : error ? (
            <Alert variant="error" title="Systemeinstellungen konnten nicht vollständig geladen werden" dismissible onDismiss={() => setError(null)}>
              {error}
            </Alert>
          ) : null}

          {loading && settings.length === 0 ? (
            <SkeletonTable rows={3} columns={3} />
          ) : settings.length === 0 ? (
            <EmptyState
              icon="Settings"
              title="Keine verwaltbaren Einstellungen"
              description="In der bestehenden settings-Tabelle sind aktuell keine Einträge vorhanden. Es werden keine neuen globalen Konfigurationen angelegt."
            />
          ) : (
            <>
              <Alert variant="info" title="Lesender Einstellungen-Überblick">
                Die angezeigten Werte stammen aus dem bestehenden Backend. Dieser Bereich bietet bewusst keine Bearbeitung sensibler oder globaler Konfigurationen.
              </Alert>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {settings.map((setting) => (
                  <div key={setting.key} className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800/40">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <code className="truncate text-xs font-semibold text-neutral-900 dark:text-neutral-100">{setting.key}</code>
                      <Badge variant="gray" size="sm">{formatDate(setting.updatedAt, true)}</Badge>
                    </div>
                    {setting.description && <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">{setting.description}</p>}
                    <div className="mt-3 rounded-md bg-neutral-50 p-3 dark:bg-neutral-900/50">
                      <p className="mb-1 text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Wert</p>
                      <code className="block max-h-32 overflow-auto whitespace-pre-wrap break-words text-xs text-neutral-800 dark:text-neutral-200">{setting.value}</code>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}